import { Pill } from "./hrUtils";
import { TONE_CLASSES } from "../assets/assetUtils";

const KIND_BG = { leave: "bg-emerald-500/80", pending: "bg-emerald-500/30 border border-emerald-500/60 border-dashed", sick: "bg-red-500/80", family: "bg-sky-500/80" };
const DOW = ["M", "T", "W", "T", "F", "S", "S"];

/** Month grid: one row per payroll employee, one column per day. Bank holidays shaded, weekends dimmed, short-cover days flagged. */
export function AbsenceGrid({ cal, onPick }) {
  const first = new Date(cal.first + "T00:00:00");
  const last = new Date(cal.last + "T00:00:00");
  const days = [];
  for (let d = new Date(first); d <= last; d.setDate(d.getDate() + 1)) days.push(new Date(d));
  const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; // local date — toISOString() shifts a day in BST
  const todayIso = iso(new Date());
  const hols = new Set((cal.holidays || []).map((h) => (typeof h === "string" ? h : h.date)));
  const short = new Set((cal.cover || []).filter((c) => c.short).map((c) => c.date));
  const byEmp = {};
  for (const it of cal.items || []) (byEmp[it.employee_id] ||= []).push(it);
  const cellFor = (empId, dayIso) => {
    const hit = (byEmp[empId] || []).find((it) => it.start <= dayIso && dayIso <= it.end);
    if (!hit) return null;
    return { ...hit, cls: hit.kind === "leave" && hit.status === "pending" ? KIND_BG.pending : KIND_BG[hit.kind] || "bg-muted" };
  };
  const emps = cal.employees || [];
  if (emps.length === 0) return <p className="text-sm text-muted-foreground" data-testid="hr-abs-grid-empty">No active payroll employees to show.</p>;
  return (
    <div className="overflow-x-auto" data-testid="hr-abs-grid">
      <table className="text-[11px] border-separate border-spacing-0 min-w-full">
        <thead>
          <tr>
            <th className="sticky left-0 bg-card text-left pr-2 py-1 font-medium text-muted-foreground">Person</th>
            {days.map((d) => { const k = iso(d); const we = d.getDay() === 0 || d.getDay() === 6; return (
              <th key={k} className={`w-6 py-1 text-center font-normal ${hols.has(k) ? "text-amber-600" : we ? "text-muted-foreground/50" : "text-muted-foreground"} ${k === todayIso ? "underline decoration-primary decoration-2" : ""}`} title={hols.has(k) ? "Bank holiday" : ""} data-testid={`hr-abs-grid-day-${k}`}>
                <div>{DOW[(d.getDay() + 6) % 7]}</div><div className="font-medium">{d.getDate()}</div>
              </th>); })}
          </tr>
        </thead>
        <tbody>
          {emps.map((e) => (
            <tr key={e.id} data-testid={`hr-abs-grid-row-${e.id}`}>
              <td className="sticky left-0 bg-card pr-2 py-0.5 whitespace-nowrap font-medium">{e.name}</td>
              {days.map((d) => { const k = iso(d); const we = d.getDay() === 0 || d.getDay() === 6; const c = cellFor(e.id, k);
                const base = hols.has(k) ? "bg-amber-500/15" : we ? "bg-muted/40" : short.has(k) ? "bg-red-500/10" : "";
                return (
                  <td key={k} className={`p-px ${base}`} title={c ? `${e.name} · ${c.label} ${c.start}${c.end !== c.start ? ` → ${c.end}` : ""}${c.status === "pending" ? " (pending)" : ""}` : short.has(k) ? "Cover short" : ""}>
                    <button onClick={() => c && onPick?.(c)} className={`block h-5 w-full rounded-sm ${c ? c.cls : "hover:bg-muted"}`} aria-label={c ? c.label : k} data-testid={c ? `hr-abs-grid-cell-${e.id}-${k}` : undefined} />
                  </td>); })}
            </tr>
          ))}
          <tr>
            <td className="sticky left-0 bg-card pr-2 py-1 text-muted-foreground">Cover</td>
            {days.map((d) => { const k = iso(d); const cv = (cal.cover || []).find((c) => c.date === k); return (
              <td key={k} className="text-center py-1" title={cv ? `${cv.available} available of ${cv.due} due (min ${cv.min})` : ""} data-testid={`hr-abs-grid-cover-${k}`}>
                {cv ? <span className={`inline-block min-w-[1.1rem] rounded px-0.5 ${cv.short ? "bg-red-500 text-white font-semibold" : "text-muted-foreground"}`}>{cv.available}</span> : <span className="text-muted-foreground/40">·</span>}
              </td>); })}
          </tr>
        </tbody>
      </table>
      <div className="mt-2 flex flex-wrap gap-2 items-center text-[11px] text-muted-foreground" data-testid="hr-abs-grid-legend">
        <span className="inline-flex items-center gap-1"><i className="inline-block h-3 w-3 rounded-sm bg-emerald-500/80" /> Annual leave</span>
        <span className="inline-flex items-center gap-1"><i className={`inline-block h-3 w-3 rounded-sm ${KIND_BG.pending}`} /> Pending</span>
        <span className="inline-flex items-center gap-1"><i className="inline-block h-3 w-3 rounded-sm bg-red-500/80" /> Sickness</span>
        <span className="inline-flex items-center gap-1"><i className="inline-block h-3 w-3 rounded-sm bg-sky-500/80" /> Family leave</span>
        <span className="inline-flex items-center gap-1"><i className="inline-block h-3 w-3 rounded-sm bg-amber-500/30" /> Bank holiday</span>
        <span className="inline-flex items-center gap-1"><i className="inline-block h-3 w-3 rounded-sm bg-red-500/10 border border-red-500/40" /> Cover short</span>
        {cal.min_cover > 0 && <Pill tone={TONE_CLASSES.none}>Minimum cover {cal.min_cover}</Pill>}
      </div>
    </div>
  );
}
